import React, { useState, useEffect, useCallback } from 'react';
import {
  FormControlLabel,
  Grid,
  Button,
  Typography,
  Checkbox,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../model/Root';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { color } from '../../theme';
import { useTranslation } from 'react-i18next';
import { User } from '../../types';
import { UserDefaults } from '../../utils/defaults';
import { UserRole } from '../../utils/constants';
import { Form, Formik, FormikProps } from 'formik';
import { MenuItem, Select } from '@mui/material';
import FormFieldText from 'components/FormComponents/FormFieldText';
import * as Yup from 'yup';
import FormFieldCheckbox from 'components/FormComponents/FormFieldCheckbox';
import FormFieldCheckboxList from 'components/FormComponents/FormFieldCheckboxList';
import DoneIcon from '@mui/icons-material/Done';
import BlockIcon from '@mui/icons-material/Block';
import UserSubscriptions from './UserSubscriptions';
import UserStripeSubscriptions from './UserStripeSubscriptions';
import { resetUserUsage } from 'services/api';
import { getSnapshot } from 'mobx-state-tree';

const getValidationSchema = (t: (key: string) => string) => {
  return Yup.object().shape({
    firstname: Yup.string().required(t('common_required')),
    lastname: Yup.string().required(t('common_required')),
    email: Yup.string().required(t('common_required')),
  });
};

const UsersEditScreen = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const { pathname } = window.location;
  const isEmployee = pathname.includes('emp');
  const isPortal = pathname.includes('portal');
  const [isEdit, setIsEdit] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User>(UserDefaults);
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const {
    userStore: {
      singleUser,
      communities,
      getUserById,
      updateUser,
      createUser,
      deleteUser,
      getCommunities,
      stripeSubscriptions,
      stripeSubscriptionsLoading,
      stripeSubscriptionsUserId,
      getStripeSubscriptions,
      products,
      getProducts,
      clearSingleUser,
      clearStripeSubscriptions,
      state: authState,
    },
    churchStore: { churches, fetchChurches },
    errorStore: { setError, setSuccess },
  } = useMst();

  const getUser = useCallback(
    async (id?: string) => {
      if (!id) return;

      setIsEdit(true);
      await getUserById(id);
      await getStripeSubscriptions(id);
    },
    [getUserById, getStripeSubscriptions]
  );

  useEffect(() => {
    if (userId !== 'create') {
      getUser(userId);
    } else {
      setSelectedUser(UserDefaults);
    }

    return () => {
      clearSingleUser();
      clearStripeSubscriptions();
    };
  }, [userId, getUser, clearSingleUser, clearStripeSubscriptions]);

  useEffect(() => {
    if (singleUser) {
      setSelectedUser(getSnapshot(singleUser) as User);
    } else {
      setSelectedUser(UserDefaults);
    }
  }, [singleUser]);

  useEffect(() => {
    getCommunities();
    getProducts();
    if (churches.length === 0) fetchChurches('', true);
  }, [getCommunities, getProducts, fetchChurches, churches.length]);

  // Ensure churches load as soon as auth is ready (race-safe)
  useEffect(() => {
    if (authState === 'LOGGED_IN' && churches.length === 0) {
      fetchChurches('', true);
    }
  }, [authState, churches.length, fetchChurches]);

  const getUserUrl = () => {
    if (isEmployee) return '/emp-users';
    if (isPortal) return '/portal-users';

    return '/admin-users';
  };

  const saveUser = async (values: User) => {
    // Coerce boolean flags explicitly to booleans and whitelist payload keys
    const booleanKeys = [
      'admin',
      'publisher',
      'creator',
      'employee',
      'portal',
      'subscriber',
      'tester',
      'permission',
      'marketing',
      'newsletter',
      'empNewsletter',
      'emailVerified',
    ] as const;

    const allowedKeys = [
      'id',
      'firstname',
      'lastname',
      'email',
      'churchId',
      'address',
      'postalcode',
      'city',
      'country',
      'phone',
      ...booleanKeys,
      'customerId',
      'communityIds',
      'subscriptionTypeId',
      'subscriptionMonths',
      'nextUsageResetDate',
    ] as const;

    type AllowedKey = (typeof allowedKeys)[number];
    type BooleanKey = (typeof booleanKeys)[number];

    const isBooleanKey = (key: AllowedKey): key is BooleanKey => (booleanKeys as readonly AllowedKey[]).includes(key);

    const payload: Partial<Record<AllowedKey, User[AllowedKey]>> = {};

    allowedKeys.forEach((key) => {
      const value = values[key as keyof User];

      if (isBooleanKey(key)) {
        payload[key] = Boolean(value) as User[AllowedKey];
      } else if (key === 'communityIds') {
        payload[key] = (Array.isArray(value) ? value : []) as User[AllowedKey];
      } else if (key === 'subscriptionTypeId' || key === 'subscriptionMonths') {
        if (value !== undefined && value !== null) {
          payload[key] = Number(value) as User[AllowedKey];
        }
      } else if (value !== undefined) {
        payload[key] = value as User[AllowedKey];
      }
    });

    if (userId == 'create') {
      // Remove id field for new user creation since backend expects types.number, not null
      const userDataWithoutId = { ...payload } as Record<string, unknown>;
      delete userDataWithoutId.id;

      // Remove values that should be null according to backend UserModel
      const cleanUserData = Object.fromEntries(
        Object.entries(userDataWithoutId).filter(([key, value]) => {
          // Remove null and undefined values
          if (value === null || value === undefined) return false;

          // Remove default values for fields that are maybeNull in backend
          if (key === 'subscriptionTypeId' && value === 0) return false;
          if (key === 'subscriptionMonths' && value === 0) return false;
          if (key === 'nextUsageResetDate' && value === '') return false;

          return true;
        })
      );

      await createUser(cleanUserData);
    } else {
      await updateUser(userId, payload);
    }

    navigate(getUserUrl());
  };

  const deleteHandler = async () => {
    if (selectedUser?.id) {
      await deleteUser(selectedUser.id, isEmployee ? UserRole.employee : UserRole.admin);
      navigate(getUserUrl());
    }
  };

  const handleResetUsage = async () => {
    if (userId) {
      try {
        await resetUserUsage(userId);
        setSuccess(t('user_reset_usage_success'));
      } catch (error) {
        setError(t('user_reset_usage_error'));
      }
    }
    setOpenResetDialog(false);
  };

  const reloadUser = async () => {
    await getUser(userId);
  };

  const renderRadioGroup = () => (
    <Grid container mt={2}>
      <Grid item xs={12}>
        <Typography fontSize={18} fontWeight={'bold'}>
          {t('user_role')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <FormFieldCheckbox name="admin" label={t('admin')} />
      </Grid>
      <Grid item xs={12}>
        <FormFieldCheckbox name="publisher" label={t('publisher')} />
      </Grid>
      <Grid item xs={12}>
        <FormFieldCheckbox name="creator" label={t('creator')} />
      </Grid>
      <Grid item xs={12}>
        <FormFieldCheckbox name="employee" label={t('employee')} />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          id={'portal'}
          control={<Checkbox checked={true} color={'primary'} />}
          label={t('portal')}
          disabled
        />
      </Grid>
      <Grid item xs={12}>
        <FormFieldCheckbox name="subscriber" label={t('subscriber')} />
        <br />
        <Typography variant="caption" color="graytext">
          {t('subscriber_helptext')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <FormFieldCheckbox name="tester" label={t('tester')} />
        <br />
        <Typography variant="caption" color="graytext">
          {t('tester_helptext')}
        </Typography>
      </Grid>
    </Grid>
  );

  const renderPermissionRadioGroup = () => (
    <Grid container mt={2}>
      <Grid item xs={12}>
        <Typography fontSize={18} fontWeight={'bold'}>
          {t('permissions')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <FormFieldCheckbox name="permission" label={t('permission')} />
      </Grid>
      <Grid item xs={12}>
        <FormFieldCheckbox name="marketing" label={t('marketing')} />
      </Grid>
      <Grid item xs={12}>
        <FormFieldCheckbox name="newsletter" label={t('newsletter')} />
      </Grid>
      <Grid item xs={12}>
        <FormFieldCheckbox name="empNewsletter" label={t('empNewsletter')} />
      </Grid>
    </Grid>
  );

  const renderHeader = () => {
    let userRole = 'admin'; // bad default!!
    if (isEmployee) {
      userRole = 'employee';
    } else if (isPortal) {
      userRole = 'portal';
    }

    return t(`${userRole}_users_${isEdit ? 'edit' : 'add'}`);
  };

  if (!selectedUser) return null;
  return (
    <>
      <Formik
        initialValues={{
          ...selectedUser,
          communityIds:
            selectedUser.communities?.filter((item) => (item.id ?? 0) > 0).map((item) => item.id ?? 0) ?? [],
        }}
        enableReinitialize
        onSubmit={(values: User) => {
          saveUser(values);
        }}
        validationSchema={getValidationSchema(t)}
      >
        {({ dirty, isValid, values, setFieldValue }: FormikProps<User>) => {
          return (
            <Form>
              <Layout title={renderHeader()} fullWidth>
                <Grid container spacing={2}>
                  <Grid container spacing={2} item md={6} pr={4}>
                    <Grid item xs={12}>
                      <FormFieldText name="firstname" label={t('first_name')} />
                    </Grid>
                    <Grid item xs={12}>
                      <FormFieldText name="lastname" label={t('last_name')} />
                    </Grid>
                    <Grid item xs={11}>
                      <FormFieldText name="email" label={t('email')} />
                    </Grid>
                    <Grid item xs={1} mt={1}>
                      {values.emailVerified ? (
                        <DoneIcon color="success" titleAccess={t('emailVerified') ?? ''} />
                      ) : (
                        <BlockIcon color="warning" titleAccess={t('emailNotVerified') ?? ''} />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <FormFieldText name="phone" label={t('phone')} />
                    </Grid>
                    <Grid item xs={12}>
                      <FormFieldText name="address" label={t('address')} />
                    </Grid>
                    <Grid item xs={4}>
                      <FormFieldText name="postalcode" label={t('postalcode')} />
                    </Grid>
                    <Grid item xs={8}>
                      <FormFieldText name="city" label={t('city')} />
                    </Grid>
                    <Grid item xs={12}>
                      <FormFieldText name="country" label={t('country')} />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography fontSize={18} fontWeight={'bold'}>
                        {t('church.title')}
                      </Typography>
                      <Select
                        fullWidth
                        size="small"
                        value={values.churchId ?? 'null'}
                        onChange={(e) =>
                          setFieldValue('churchId', e.target.value === 'null' ? null : (e.target.value as string))
                        }
                      >
                        <MenuItem value={'null'}>Ei seurakuntaa</MenuItem>
                        {churches.map((c) => (
                          <MenuItem key={c.id} value={c.id ?? ''}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                  </Grid>
                  <Grid container item md={6}>
                    <FormFieldCheckboxList
                      label={t('communities')}
                      name="communityIds"
                      listItems={communities.map((item) => ({ id: item.id, name: item.name }))}
                    />
                  </Grid>
                  <Grid container item md={6} style={{ alignContent: 'flex-start' }}>
                    {renderRadioGroup()}
                  </Grid>
                  <Grid container item md={6} style={{ alignContent: 'flex-start' }}>
                    {renderPermissionRadioGroup()}
                  </Grid>
                  <Grid container item xs={12} spacing={2} mt={2}>
                    <Grid item>
                      <Button variant="contained" type="submit" disabled={!dirty || !isValid}>
                        {t('save')}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        onClick={deleteHandler}
                        style={{ backgroundColor: color.deleteButton, color: color.white }}
                      >
                        {t('delete')}
                      </Button>
                    </Grid>
                    {/* HIDDEN: Dangerous button that removes usage data instead of just resetting listening time */}
                    {/* {isEdit && (
                      <Grid item>
                        <Button
                          onClick={() => setOpenResetDialog(true)}
                          style={{ backgroundColor: color.warning, color: color.white }}
                        >
                          {t('user_reset_listening_time')}
                        </Button>
                      </Grid>
                    )} */}
                  </Grid>
                </Grid>
                <Grid item mt={3}>
                  <Divider />
                </Grid>
                {selectedUser?.subscriptions && selectedUser.id && (
                  <UserSubscriptions reloadUser={reloadUser} currentUser={selectedUser} />
                )}
                {userId !== 'create' && stripeSubscriptionsUserId === userId && (
                  <UserStripeSubscriptions
                    subscriptions={stripeSubscriptions}
                    products={products}
                    customerId={selectedUser?.customerId}
                    loading={stripeSubscriptionsLoading}
                  />
                )}
                {values.nextUsageResetDate && (
                  <Grid item xs={12} mt={3}>
                    <Typography variant="body2">
                      <b>{t('user_next_usage_reset_date')}:</b>{' '}
                      {values.nextUsageResetDate
                        ? new Date(values.nextUsageResetDate).toLocaleDateString('fi-FI')
                        : t('subscription_management.null_value')}
                    </Typography>
                  </Grid>
                )}
              </Layout>
            </Form>
          );
        }}
      </Formik>
      <Dialog open={openResetDialog} onClose={() => setOpenResetDialog(false)}>
        <DialogTitle>{t('user_reset_listening_time_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('user_reset_listening_time_confirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResetDialog(false)} color="primary">
            {t('cancel')}
          </Button>
          <Button onClick={handleResetUsage} color="warning" autoFocus>
            {t('reset')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default UsersEditScreen;
